'use client';

import { useState } from 'react';
import { useLocale } from 'next-intl';
import { LocaleCode } from '@/i18n/locales';
import { Button } from '@/shared/components/primitives/button';
import { Loader } from '@/shared/components/primitives/loader';
import { getCountryCodeFromOneTrust } from '@/shared/utils/get-country-code-from-one-trust';
import { fetchDownLoadUrl } from '@/features/games-app/internal/helpers/build-download-link';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useCurrency } from '@/features/shared/hooks/use-currency';

type GetAppButtonProps = {
  buttonLabel: string;
  loadingLabel: string;
};

export const GetAppButton = ({ buttonLabel, loadingLabel }: GetAppButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const currentCurrency = useCurrency();
  const { authenticatedUser } = useUser();

  const currentLocale = useLocale() as LocaleCode;
  const currentCountryCode = getCountryCodeFromOneTrust();

  const handleDownload = async () => {
    setIsLoading(true);
    const downloadUrl = await fetchDownLoadUrl({
      activeCurrency: currentCurrency,
      language: currentLocale,
      email: authenticatedUser?.email,
      countryCode: currentCountryCode,
    });

    if (typeof downloadUrl !== 'string') {
      setIsLoading(false);
      return;
    }

    window.location.assign(downloadUrl);
    setIsLoading(false);
  };

  return (
    <Button
      id='download-games-app'
      className='w-40'
      onClick={handleDownload}
      disabled={isLoading}
      aria-label={isLoading ? loadingLabel : buttonLabel}
    >
      {isLoading ? (
        <>
          <Loader size={16} />
          <span className='ml-2'>{loadingLabel}</span>
        </>
      ) : (
        <span>{buttonLabel}</span>
      )}
    </Button>
  );
};
